import { animateScroll as scroll } from 'react-scroll';
import selectn from 'selectn';
import { consoleError, consoleWarn } from 'utils/log';

/**
 * Waits onload event to trigger
 */
export function onLoad(applyTimeout?: number) {
  return new Promise((resolve) => {
    window.addEventListener('load', () => {
      if (!applyTimeout && applyTimeout !== 0) {
        resolve(true);
      } else {
        setTimeout(() => {
          resolve(true);
        }, applyTimeout);
      }
    });
  });
}

/**
 * Finds element and scrolls into it - supports IE and Safari as well (unlike CSS "behaviour: 'smooth'")
 * @param attrValue
 * @param attrName
 * @param duration
 * @param opts
 */
export function scrollToElement(attrValue: string, attrName = 'id', duration = 500, opts = {}) {
  const scrollToEl: HTMLElement = document.querySelector(`[${attrName}="${attrValue}"]`);

  if (scrollToEl) {
    scroll.scrollTo(+scrollToEl.offsetTop, {
      smooth: true,
      duration,
      offset: 0,
      ...opts,
    });
  } else {
    consoleWarn('Scroll target not found', `[${attrName}="${attrValue}"]`);
  }
}

/**
 * Scrolls page completely to the top
 */
export function scrollTop() {
  scroll.scrollTo(0);
}

/**
 * Scrolls page to the bottom
 */
export function scrollBottom(timeout?: number) {
  if (!timeout && timeout !== 0) {
    scroll.scrollToBottom();

    return;
  }

  setTimeout(() => {
    scroll.scrollToBottom();
  }, timeout);
}

/**
 * Sorts array of object by nested key which value is string
 * @param array
 * @param nestedKeys
 */
export function sortArrayOfObjects(array: Array<any>, ...nestedKeys) {
  return array.sort((a, b) => {
    const textA = nestedKeys
      .map((nestedKey) => selectn(nestedKey, a) || '')
      .join('')
      .replace(/"/gim, '') // Remove quotes that affect sorting
      .toUpperCase();
    const textB = nestedKeys
      .map((nestedKey) => selectn(nestedKey, b) || '')
      .join('')
      .replace(/"/gim, '')
      .toUpperCase();

    return textA < textB ? -1 : textA > textB ? 1 : 0;
  });
}

/**
 * Capitalize first letter of the whole string. Is used in case CSS can't help us:
 * - CSS "text-transform: capitalize;" capitalizes first letter of every word in the string.
 * - CSS "::first-letter" won't work on inline elements.
 * @param text
 */
export function capitalize(text: string) {
  if (typeof text !== 'string') {
    return text;
  }

  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Crossbrowser way to get body scroll position x
 */
export function getBodyScrollPositionX(): number {
  return Math.max(
    document.documentElement && document.documentElement.scrollLeft,
    document.body && document.body.scrollLeft,
    document.scrollingElement && document.scrollingElement.scrollLeft,
  );
}

/**
 * Crossbrowser way to get body scroll position y
 */
export function getBodyScrollPositionY(): number {
  return Math.max(
    document.documentElement && document.documentElement.scrollTop,
    document.body && document.body.scrollTop,
    document.scrollingElement && document.scrollingElement.scrollTop,
  );
}

/**
 * Crossbrowser way to get body scroll height
 */
export function getBodyScrollHeight() {
  return Math.max(
    document.documentElement && document.documentElement.scrollHeight,
    document.body && document.body.scrollHeight,
    document.scrollingElement && document.scrollingElement.scrollHeight,
  );
}

/**
 * Copy text to clipboard
 * @param content
 */
export function copyToClipboard(content: string): boolean {
  if (!content) {
    return false;
  }

  const newInputEl = document.createElement('textarea');

  document.body.appendChild(newInputEl);
  newInputEl.value = content;
  newInputEl.select();

  try {
    return document.execCommand('copy', false);
  } catch {
    return false;
  } finally {
    newInputEl.remove();
  }
}

/**
 * Gets text from HTML string
 * @param message
 */
export function extractMessageFromHTML(message: string) {
  try {
    const span = document.createElement('span');

    span.innerHTML = message;
    const text = span.textContent || span.innerText;

    span.remove();

    return text;
  } catch (err) {
    consoleError('Something went wrong when parsing user message in messages list');
  }
}
